import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui-hud";
import { CheckCircle2, Loader2, XCircle, ArrowRight, CreditCard } from "lucide-react";

const MAX_POLLS = 10;

export default function HireCardSaved() {
  const [params] = useSearchParams();
  const hireId = params.get("hire_id");
  const [state, setState] = useState({ phase: "polling" });
  const attempts = useRef(0);

  useEffect(() => {
    if (!hireId) { setState({ phase: "error" }); return; }
    let alive = true;
    const tick = async () => {
      if (!alive) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/hire-crew-now/card-status/${hireId}`);
        if (!alive) return;
        if (r.data.card_status === "saved") {
          setState({ phase: "saved", pm: r.data.payment_method_id });
          return;
        }
      } catch { /* keep polling */ }
      if (attempts.current >= MAX_POLLS) {
        setState({ phase: "timeout" });
        return;
      }
      setTimeout(tick, 1500);
    };
    tick();
    return () => { alive = false; };
  }, [hireId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <Card className="max-w-lg w-full text-center">
        {state.phase === "polling" && (
          <>
            <Loader2 className="h-10 w-10 text-cyan mx-auto animate-spin mb-4" />
            <div className="overline text-cyan mb-2">CONFIRMING CARD</div>
            <h1 className="font-display text-2xl font-bold tracking-tight mb-2">Verifying with Stripe…</h1>
            <p className="text-sm text-white/60">Your card is being saved on file — no charge now.</p>
          </>
        )}
        {state.phase === "saved" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto mb-3" />
            <div className="overline text-emerald-400 mb-2">CARD VERIFIED</div>
            <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Trip guaranteed.</h1>
            <p className="text-sm text-white/60 mb-2">Card saved. You will be charged at trip completion; funds released to the crew's PJP wallet.</p>
            <div className="inline-flex items-center gap-1.5 mono text-[11px] text-white/50 bg-white/5 border border-white/10 px-3 py-1 rounded-full mb-6"><CreditCard className="h-3 w-3" /> STRIPE · {String(state.pm || "").slice(-10)}</div>
            <div className="mt-2 flex gap-3 justify-center">
              <Link to="/dashboard" data-testid="btn-saved-dashboard" className="btn btn-primary">GO TO COCKPIT <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          </>
        )}
        {state.phase === "timeout" && (
          <>
            <Loader2 className="h-10 w-10 text-cyan mx-auto mb-4" />
            <div className="overline text-cyan mb-2">STILL PROCESSING</div>
            <h1 className="font-display text-2xl font-bold tracking-tight mb-2">Stripe is taking longer than usual.</h1>
            <p className="text-sm text-white/60">Refresh in a minute. Your card details are safe.</p>
          </>
        )}
        {state.phase === "error" && (
          <>
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold tracking-tight">Missing hire reference.</h1>
            <Link to="/hire-now" className="btn mt-4 inline-flex">BACK TO HIRE FLOW</Link>
          </>
        )}
      </Card>
    </div>
  );
}
